import React, { ReactElement, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

type TooltipProps = {
  message: string;
  children: ReactElement;
  position?: 'top' | 'bottom';
};

const Tooltip: React.FC<TooltipProps> = ({ message, children, position = 'bottom' }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({});
  const ref = useRef<HTMLSpanElement>(null);

  const updatePosition = () => {
    if (!ref.current || !showTooltip) return;
    
    const rect = ref.current.getBoundingClientRect();
    
    setStyle({
      position: 'absolute',
      left: rect.left + window.scrollX + rect.width / 2,
      top: rect.bottom + window.scrollY + 4,
      transform: 'translateX(-50%)',
      zIndex: 9999
    });
  };

  useEffect(() => {
    if (showTooltip) {
      updatePosition();
      const handler = () => updatePosition();
      window.addEventListener('scroll', handler);
      window.addEventListener('resize', handler);
      return () => {
        window.removeEventListener('scroll', handler);
        window.removeEventListener('resize', handler);
      };
    }
  }, [showTooltip]);

  // Hide tooltip when modal opens (listen for custom event)
  useEffect(() => {
    const hideTooltip = () => setShowTooltip(false);
    document.addEventListener('hideTooltips', hideTooltip);
    return () => document.removeEventListener('hideTooltips', hideTooltip);
  }, []);

  const tooltip = showTooltip && (
    <div style={style} className="pointer-events-none absolute z-20 transition-opacity duration-150">
      <div className="flex items-center flex-col-reverse">
        <div className="rounded bg-gray-800 p-2 text-center text-caption text-white whitespace-nowrap">
          {message}
        </div>
        
        <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800" style={{ marginBottom: '-1px' }} />
      </div>
    </div>
  );

  return (
    <span 
      ref={ref}
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {typeof document !== 'undefined' && createPortal(tooltip, document.body)}
    </span>
  );
};

export default Tooltip;
